import React, { useState, useEffect, useCallback } from 'react';
import { paymentsService, gdService, invoiceService } from '../../services/financeService';
import { buildCashTransactionRows, buildCheckRows, buildPayOrderRows } from '../../utils/exportUtils';
import AttachmentPanel from './AttachmentPanel';
import CsvActions from '../common/CsvActions';
import './Financial.css';
import './PaymentsManagement.css';

const formatEnum = (val) =>
  val ? val.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const formatCurrency = (amount, currency = 'PKR') => {
  if (amount == null) return '—';
  return `${currency} ${Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Amounts in different currencies must never be blended into one number — render each
// currency in the map on its own line instead.
const CurrencyBreakdown = ({ byCurrency }) => {
  const entries = Object.entries(byCurrency || {});
  if (entries.length === 0) return <div className="pm-card-value">{formatCurrency(0)}</div>;
  return (
    <>
      {entries.map(([currency, amount]) => (
        <div className="pm-card-value" key={currency}>{formatCurrency(amount, currency)}</div>
      ))}
    </>
  );
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const StatusBadge = ({ value, colorMap }) => {
  const color = colorMap?.[value] || 'gray';
  const colors = {
    green: { background: 'var(--color-success-light)', color: 'var(--color-success)' },
    red: { background: 'var(--color-error-light)', color: 'var(--color-error)' },
    yellow: { background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)' },
    blue: { background: 'var(--color-info-light)', color: 'var(--color-primary)' },
    orange: { background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)' },
    purple: { background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)' },
    gray: { background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' },
  };
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      ...colors[color],
    }}>
      {formatEnum(value)}
    </span>
  );
};

const CASH_TYPE_COLORS = { CASH_IN: 'green', CASH_OUT: 'red' };
const CHECK_STATUS_COLORS = {
  ISSUED: 'blue', DEPOSITED: 'yellow', CLEARED: 'green',
  BOUNCED: 'red', CANCELLED: 'gray', STOPPED: 'orange',
};
const CHECK_TYPE_COLORS = { ISSUED: 'orange', RECEIVED: 'purple' };
const PAY_ORDER_STATUS_COLORS = { ISSUED: 'blue', CLEARED: 'green', CANCELLED: 'gray' };

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'cash', label: 'Cash Register' },
  { id: 'checks', label: 'Checks' },
  { id: 'payorders', label: 'Pay Orders' },
];

const CASH_CATEGORIES = [
  'VENDOR_PAYMENT', 'CUSTOMER_RECEIPT', 'SALARY', 'LOGISTICS',
  'CLEARING', 'TRANSPORT', 'ADVANCE', 'REFUND', 'UTILITY', 'OTHER',
];
const PARTY_TYPES = ['VENDOR', 'SUPPLIER', 'CUSTOMER', 'EMPLOYEE', 'OTHER'];
const CHECK_STATUSES = ['ISSUED', 'DEPOSITED', 'CLEARED', 'BOUNCED', 'CANCELLED', 'STOPPED'];

export default function PaymentsManagement() {
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [cashList, setCashList] = useState([]);
  const [checkList, setCheckList] = useState([]);
  const [payOrderList, setPayOrderList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pagination
  const [cashPage, setCashPage] = useState(0);
  const [cashTotal, setCashTotal] = useState(0);
  const [checkPage, setCheckPage] = useState(0);
  const [checkTotal, setCheckTotal] = useState(0);
  const [poPage, setPoPage] = useState(0);
  const [poTotal, setPoTotal] = useState(0);

  // Filters
  const [cashSearch, setCashSearch] = useState('');
  const [cashTypeFilter, setCashTypeFilter] = useState('');
  const [checkSearch, setCheckSearch] = useState('');
  const [checkStatusFilter, setCheckStatusFilter] = useState('');
  const [checkTypeFilter, setCheckTypeFilter] = useState('');
  const [poSearch, setPoSearch] = useState('');
  const [poStatusFilter, setPoStatusFilter] = useState('');

  // Modals
  const [showCashModal, setShowCashModal] = useState(false);
  const [showCheckModal, setShowCheckModal] = useState(false);
  const [showPoModal, setShowPoModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailType, setDetailType] = useState('');

  // Status update modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusItem, setStatusItem] = useState(null);
  const [statusItemType, setStatusItemType] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);

  // Edit mode (reuses create modals)
  const [editingItem, setEditingItem] = useState(null);

  // Delete confirm modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteItemType, setDeleteItemType] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast notification
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  // Forms
  const [cashForm, setCashForm] = useState({
    type: 'CASH_IN', transactionDate: new Date().toISOString().split('T')[0],
    amount: '', currency: 'PKR', counterpartyName: '', counterpartyType: 'VENDOR',
    category: 'OTHER', description: '', referenceNumber: '', notes: '',
    linkedGDId: '', linkedInvoiceId: '',
  });
  const [checkForm, setCheckForm] = useState({
    checkNumber: '', bankName: '', accountNumber: '', checkType: 'ISSUED',
    amount: '', currency: 'PKR', partyName: '', partyType: 'VENDOR',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '', purpose: '', referenceNumber: '', notes: '',
    linkedGDId: '', linkedInvoiceId: '',
  });
  const [poForm, setPoForm] = useState({
    bankName: '', amount: '', currency: 'PKR', beneficiaryName: '',
    beneficiaryBank: '', beneficiaryAccount: '', purpose: '',
    issueDate: new Date().toISOString().split('T')[0], referenceNumber: '', notes: '',
    linkedGDId: '', linkedInvoiceId: '',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [gdOptions, setGdOptions] = useState([]);
  const [invoiceOptions, setInvoiceOptions] = useState([]);

  const loadSummary = useCallback(async () => {
    try {
      const res = await paymentsService.getSummary();
      if (res.success) setSummary(res.data);
    } catch (e) {
      setError('Failed to load summary');
    }
  }, []);

  const loadLinkedOptions = useCallback(async () => {
    try {
      const [gdRes, invRes] = await Promise.all([
        gdService.getAll({ size: 200 }).catch(() => ({ data: [] })),
        invoiceService.getAll().catch(() => []),
      ]);
      const gds = gdRes?.data?.content || gdRes?.data || [];
      setGdOptions(Array.isArray(gds) ? gds : []);
      setInvoiceOptions(Array.isArray(invRes) ? invRes : (invRes?.data || []));
    } catch (e) { setError('Failed to load linked options'); }
  }, []);

  const loadCash = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: cashPage, size: 20 };
      if (cashSearch) params.search = cashSearch;
      if (cashTypeFilter) params.type = cashTypeFilter;
      const res = await paymentsService.getCashTransactions(params);
      if (res.success) {
        setCashList(Array.isArray(res.data) ? res.data : res.data);
        setCashTotal(res.totalElements || 0);
      }
    } catch (e) {
      setError('Failed to load cash transactions');
    } finally {
      setLoading(false);
    }
  }, [cashPage, cashSearch, cashTypeFilter]);

  const loadChecks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: checkPage, size: 20 };
      if (checkSearch) params.search = checkSearch;
      if (checkStatusFilter) params.status = checkStatusFilter;
      if (checkTypeFilter) params.checkType = checkTypeFilter;
      const res = await paymentsService.getChecks(params);
      if (res.success) {
        setCheckList(Array.isArray(res.data) ? res.data : res.data);
        setCheckTotal(res.totalElements || 0);
      }
    } catch (e) {
      setError('Failed to load checks');
    } finally {
      setLoading(false);
    }
  }, [checkPage, checkSearch, checkStatusFilter, checkTypeFilter]);

  const loadPayOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: poPage, size: 20 };
      if (poSearch) params.search = poSearch;
      if (poStatusFilter) params.status = poStatusFilter;
      const res = await paymentsService.getPayOrders(params);
      if (res.success) {
        setPayOrderList(Array.isArray(res.data) ? res.data : res.data);
        setPoTotal(res.totalElements || 0);
      }
    } catch (e) {
      setError('Failed to load pay orders');
    } finally {
      setLoading(false);
    }
  }, [poPage, poSearch, poStatusFilter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (activeTab === 'cash') loadCash(); }, [activeTab, loadCash]);
  useEffect(() => { if (activeTab === 'checks') loadChecks(); }, [activeTab, loadChecks]);
  useEffect(() => { if (activeTab === 'payorders') loadPayOrders(); }, [activeTab, loadPayOrders]);

  const openEditCash = (tx) => {
    setEditingItem(tx);
    setCashForm({
      type: tx.type,
      transactionDate: tx.transactionDate,
      amount: String(tx.amount),
      currency: tx.currency || 'PKR',
      counterpartyName: tx.counterpartyName,
      counterpartyType: tx.counterpartyType,
      category: tx.category,
      description: tx.description || '',
      referenceNumber: tx.referenceNumber || '',
      notes: tx.notes || '',
      linkedGDId: tx.linkedGDId || '', linkedInvoiceId: tx.linkedInvoiceId || '',
    });
    setFormError('');
    loadLinkedOptions();
    setShowCashModal(true);
  };

  const handleCreateCash = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = {
        ...cashForm, amount: parseFloat(cashForm.amount),
        linkedGDId: cashForm.linkedGDId ? parseInt(cashForm.linkedGDId) : null,
        linkedInvoiceId: cashForm.linkedInvoiceId ? parseInt(cashForm.linkedInvoiceId) : null,
      };
      const res = editingItem
        ? await paymentsService.updateCashTransaction(editingItem.id, payload)
        : await paymentsService.createCashTransaction(payload);
      if (res.success) {
        setShowCashModal(false);
        setEditingItem(null);
        setCashForm({
          type: 'CASH_IN', transactionDate: new Date().toISOString().split('T')[0],
          amount: '', currency: 'PKR', counterpartyName: '', counterpartyType: 'VENDOR',
          category: 'OTHER', description: '', referenceNumber: '', notes: '',
          linkedGDId: '', linkedInvoiceId: '',
        });
        showToast(editingItem ? 'Cash transaction updated' : 'Cash transaction created');
        loadCash();
        loadSummary();
      } else {
        setFormError(res.message || 'Failed to save transaction');
      }
    } catch (e) {
      setFormError(e.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditCheck = (c) => {
    setEditingItem(c);
    setCheckForm({
      checkNumber: c.checkNumber,
      bankName: c.bankName,
      accountNumber: c.accountNumber || '',
      checkType: c.checkType,
      amount: String(c.amount),
      currency: c.currency || 'PKR',
      partyName: c.partyName,
      partyType: c.partyType,
      issueDate: c.issueDate,
      dueDate: c.dueDate,
      purpose: c.purpose || '',
      referenceNumber: c.referenceNumber || '',
      notes: c.notes || '',
      linkedGDId: c.linkedGDId || '', linkedInvoiceId: c.linkedInvoiceId || '',
    });
    setFormError('');
    loadLinkedOptions();
    setShowCheckModal(true);
  };

  const handleCreateCheck = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = {
        ...checkForm, amount: parseFloat(checkForm.amount),
        linkedGDId: checkForm.linkedGDId ? parseInt(checkForm.linkedGDId) : null,
        linkedInvoiceId: checkForm.linkedInvoiceId ? parseInt(checkForm.linkedInvoiceId) : null,
      };
      const res = editingItem
        ? await paymentsService.updateCheck(editingItem.id, payload)
        : await paymentsService.createCheck(payload);
      if (res.success) {
        setShowCheckModal(false);
        setEditingItem(null);
        setCheckForm({
          checkNumber: '', bankName: '', accountNumber: '', checkType: 'ISSUED',
          amount: '', currency: 'PKR', partyName: '', partyType: 'VENDOR',
          issueDate: new Date().toISOString().split('T')[0],
          dueDate: '', purpose: '', referenceNumber: '', notes: '',
          linkedGDId: '', linkedInvoiceId: '',
        });
        showToast(editingItem ? 'Check updated' : 'Check created');
        loadChecks();
        loadSummary();
      } else {
        setFormError(res.message || 'Failed to save check');
      }
    } catch (e) {
      setFormError(e.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditPayOrder = (po) => {
    setEditingItem(po);
    setPoForm({
      bankName: po.bankName,
      amount: String(po.amount),
      currency: po.currency || 'PKR',
      beneficiaryName: po.beneficiaryName,
      beneficiaryBank: po.beneficiaryBank || '',
      beneficiaryAccount: po.beneficiaryAccount || '',
      purpose: po.purpose || '',
      issueDate: po.issueDate,
      referenceNumber: po.referenceNumber || '',
      notes: po.notes || '',
      linkedGDId: po.linkedGDId || '', linkedInvoiceId: po.linkedInvoiceId || '',
    });
    setFormError('');
    loadLinkedOptions();
    setShowPoModal(true);
  };

  const handleCreatePayOrder = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);
    try {
      const payload = {
        ...poForm, amount: parseFloat(poForm.amount),
        linkedGDId: poForm.linkedGDId ? parseInt(poForm.linkedGDId) : null,
        linkedInvoiceId: poForm.linkedInvoiceId ? parseInt(poForm.linkedInvoiceId) : null,
      };
      const res = editingItem
        ? await paymentsService.updatePayOrder(editingItem.id, payload)
        : await paymentsService.createPayOrder(payload);
      if (res.success) {
        setShowPoModal(false);
        setEditingItem(null);
        setPoForm({
          bankName: '', amount: '', currency: 'PKR', beneficiaryName: '',
          beneficiaryBank: '', beneficiaryAccount: '', purpose: '',
          issueDate: new Date().toISOString().split('T')[0], referenceNumber: '', notes: '',
          linkedGDId: '', linkedInvoiceId: '',
        });
        showToast(editingItem ? 'Pay order updated' : 'Pay order created');
        loadPayOrders();
        loadSummary();
      } else {
        setFormError(res.message || 'Failed to save pay order');
      }
    } catch (e) {
      setFormError(e.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !statusItem) return;
    setStatusLoading(true);
    try {
      let res;
      if (statusItemType === 'check') {
        res = await paymentsService.updateCheckStatus(statusItem.id, newStatus);
      } else {
        res = await paymentsService.updatePayOrderStatus(statusItem.id, newStatus);
      }
      if (res.success) {
        setShowStatusModal(false);
        setStatusItem(null);
        setNewStatus('');
        showToast('Status updated successfully');
        if (statusItemType === 'check') { loadChecks(); } else { loadPayOrders(); }
        loadSummary();
      }
    } catch (e) {
      setError('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const openDeleteModal = (item, type) => {
    setDeleteItem(item);
    setDeleteItemType(type);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setDeleteLoading(true);
    try {
      let res;
      if (deleteItemType === 'cash') res = await paymentsService.deleteCashTransaction(deleteItem.id);
      else if (deleteItemType === 'check') res = await paymentsService.deleteCheck(deleteItem.id);
      else res = await paymentsService.deletePayOrder(deleteItem.id);

      if (res.success) {
        setShowDeleteModal(false);
        setDeleteItem(null);
        showToast('Record deleted successfully');
        if (deleteItemType === 'cash') { loadCash(); }
        else if (deleteItemType === 'check') { loadChecks(); }
        else { loadPayOrders(); }
        loadSummary();
      }
    } catch (e) {
      setError('Failed to delete item');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openStatusModal = (item, type) => {
    setStatusItem(item);
    setStatusItemType(type);
    setNewStatus(item.status || item.payOrderStatus || '');
    setShowStatusModal(true);
  };

  const openDetail = (item, type) => {
    setDetailItem(item);
    setDetailType(type);
    setShowDetailModal(true);
  };

  const daysUntilLabel = (days) => {
    if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: 'var(--color-error)', bg: 'var(--color-error-light)' };
    if (days === 0) return { label: 'Due today', color: 'var(--color-warning-strong)', bg: 'var(--color-warning-light)' };
    if (days <= 3) return { label: `${days}d left`, color: 'var(--color-warning-strong)', bg: 'var(--color-warning-light)' };
    return { label: `${days}d left`, color: 'var(--color-success-strong)', bg: 'var(--color-success-light)' };
  };

  return (
    <div className="pm-page">
      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)', color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)', padding: '12px 20px', borderRadius: 8, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.msg}
        </div>
      )}
      {/* Header */}
      <div className="pm-header">
        <div className="pm-header-left">
          <h1 className="pm-title">Payments & Cash</h1>
          <p className="pm-subtitle">Cash register, checks, and pay orders</p>
        </div>
        <div className="pm-header-actions">
          {activeTab === 'cash' && (() => { const { headers, matrix } = buildCashTransactionRows(cashList); return (
            <CsvActions filename="cash_transactions" headers={headers} matrix={matrix} className="pm-btn pm-btn-secondary" />
          ); })()}
          {activeTab === 'checks' && (() => { const { headers, matrix } = buildCheckRows(checkList); return (
            <CsvActions filename="check_register" headers={headers} matrix={matrix} className="pm-btn pm-btn-secondary" />
          ); })()}
          {activeTab === 'payorders' && (() => { const { headers, matrix } = buildPayOrderRows(payOrderList); return (
            <CsvActions filename="pay_orders" headers={headers} matrix={matrix} className="pm-btn pm-btn-secondary" />
          ); })()}
          {activeTab === 'cash' && (
            <button className="pm-btn pm-btn-primary" onClick={() => { setFormError(''); setEditingItem(null); loadLinkedOptions(); setShowCashModal(true); }}>
              + Cash Entry
            </button>
          )}
          {activeTab === 'checks' && (
            <button className="pm-btn pm-btn-primary" onClick={() => { setFormError(''); setEditingItem(null); loadLinkedOptions(); setShowCheckModal(true); }}>
              + Add Check
            </button>
          )}
          {activeTab === 'payorders' && (
            <button className="pm-btn pm-btn-primary" onClick={() => { setFormError(''); setEditingItem(null); loadLinkedOptions(); setShowPoModal(true); }}>
              + Pay Order
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="pm-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`pm-tab ${activeTab === tab.id ? 'pm-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === 'overview' && summary?.upcomingDueCount > 0 && (
              <span className="pm-badge-alert">{summary.upcomingDueCount}</span>
            )}
          </button>
        ))}
      </div>

      {error && (
        <div className="pm-alert pm-alert-error">
          {error}
          <button onClick={() => setError('')} className="pm-alert-close">×</button>
        </div>
      )}

      {/* ============ OVERVIEW TAB ============ */}
      {activeTab === 'overview' && (
        <div className="pm-tab-content">
          {/* Summary Cards */}
          <div className="pm-summary-grid">
            <div className="pm-summary-card pm-card-green">
              <div className="pm-card-label">Cash In (This Month)</div>
              <CurrencyBreakdown byCurrency={summary?.cashInThisMonth} />
            </div>
            <div className="pm-summary-card pm-card-red">
              <div className="pm-card-label">Cash Out (This Month)</div>
              <CurrencyBreakdown byCurrency={summary?.cashOutThisMonth} />
            </div>
            <div className={`pm-summary-card ${Object.values(summary?.netCashThisMonth || {}).some(v => v < 0) ? 'pm-card-orange' : 'pm-card-blue'}`}>
              <div className="pm-card-label">Net Position (This Month)</div>
              <CurrencyBreakdown byCurrency={summary?.netCashThisMonth} />
            </div>
            <div className="pm-summary-card pm-card-purple">
              <div className="pm-card-label">Upcoming Due Checks</div>
              <div className="pm-card-value">{summary?.upcomingDueCount ?? 0}</div>
              <div className="pm-card-sub">within 7 days</div>
            </div>
          </div>

          {/* Outstanding Summary Row */}
          <div className="pm-outstanding-row">
            <div className="pm-outstanding-item">
              <span className="pm-outstanding-label">Checks Issued (Outstanding)</span>
              <span className="pm-outstanding-value">{formatCurrency(summary?.checksIssuedOutstanding)}</span>
            </div>
            <div className="pm-outstanding-item">
              <span className="pm-outstanding-label">Checks Received (Outstanding)</span>
              <span className="pm-outstanding-value">{formatCurrency(summary?.checksReceivedOutstanding)}</span>
            </div>
            <div className="pm-outstanding-item">
              <span className="pm-outstanding-label">Pay Orders Issued</span>
              <span className="pm-outstanding-value">{summary?.payOrdersIssuedCount ?? 0}</span>
            </div>
          </div>

          {/* Upcoming Due Checks Alert */}
          {summary?.upcomingDueChecks?.length > 0 && (
            <div className="pm-upcoming-section">
              <div className="pm-upcoming-header">
                <span className="pm-upcoming-icon">!</span>
                <span>Upcoming Due Checks — Action Required</span>
              </div>
              <div className="pm-upcoming-list">
                {summary.upcomingDueChecks.map((c) => {
                  const due = daysUntilLabel(c.daysUntilDue);
                  return (
                    <div key={c.id} className="pm-upcoming-item">
                      <div className="pm-upcoming-left">
                        <div className="pm-upcoming-name">{c.partyName}</div>
                        <div className="pm-upcoming-meta">
                          {c.checkNumber} · {c.bankName} ·{' '}
                          <StatusBadge value={c.checkType} colorMap={CHECK_TYPE_COLORS} />
                        </div>
                      </div>
                      <div className="pm-upcoming-right">
                        <div className="pm-upcoming-amount">{formatCurrency(c.amount, c.currency)}</div>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: due.bg, color: due.color }}>
                          {due.label}
                        </span>
                        <div className="pm-upcoming-date">Due: {formatDate(c.dueDate)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!summary?.upcomingDueChecks || summary.upcomingDueChecks.length === 0) && summary && (
            <div className="pm-empty-state">
              <div className="pm-empty-icon">—</div>
              <div className="pm-empty-title">All Clear</div>
              <div className="pm-empty-sub">No checks due within the next 7 days.</div>
            </div>
          )}
        </div>
      )}

      {/* ============ CASH REGISTER TAB ============ */}
      {activeTab === 'cash' && (
        <div className="pm-tab-content">
          {/* Filters */}
          <div className="pm-filters">
            <input
              className="pm-input pm-search"
              placeholder="Search by name, number, description..."
              value={cashSearch}
              onChange={(e) => { setCashSearch(e.target.value); setCashPage(0); }}
            />
            <select
              className="pm-select"
              value={cashTypeFilter}
              onChange={(e) => { setCashTypeFilter(e.target.value); setCashPage(0); }}
            >
              <option value="">All Types</option>
              <option value="CASH_IN">Cash In</option>
              <option value="CASH_OUT">Cash Out</option>
            </select>
          </div>

          {loading ? (
            <div className="pm-loading">Loading...</div>
          ) : cashList.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-icon">—</div>
              <div className="pm-empty-title">No Cash Transactions</div>
              <div className="pm-empty-sub">Record your first cash in or out entry.</div>
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Party</th>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cashList.map((tx) => (
                    <tr key={tx.id}>
                      <td><span className="pm-mono">{tx.transactionNumber}</span></td>
                      <td>{formatDate(tx.transactionDate)}</td>
                      <td><StatusBadge value={tx.type} colorMap={CASH_TYPE_COLORS} /></td>
                      <td>
                        <div className="pm-party-name">{tx.counterpartyName}</div>
                        <div className="pm-party-type">{formatEnum(tx.counterpartyType)}</div>
                      </td>
                      <td>{formatEnum(tx.category)}</td>
                      <td className={tx.type === 'CASH_IN' ? 'pm-amount-in' : 'pm-amount-out'}>
                        {formatCurrency(tx.amount, tx.currency)}
                      </td>
                      <td>
                        <button className="pm-btn-link" onClick={() => openDetail(tx, 'cash')}>View</button>
                        <button className="pm-btn-link pm-link-edit" onClick={() => openEditCash(tx)}>Edit</button>
                        <button className="pm-btn-link pm-link-danger" onClick={() => openDeleteModal(tx, 'cash')}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {cashTotal > 20 && (
            <div className="pm-pagination">
              <button disabled={cashPage === 0} onClick={() => setCashPage(p => p - 1)} className="pm-btn pm-btn-sm">Prev</button>
              <span>Page {cashPage + 1} of {Math.ceil(cashTotal / 20)}</span>
              <button disabled={(cashPage + 1) * 20 >= cashTotal} onClick={() => setCashPage(p => p + 1)} className="pm-btn pm-btn-sm">Next</button>
            </div>
          )}
        </div>
      )}

      {/* ============ CHECKS TAB ============ */}
      {activeTab === 'checks' && (
        <div className="pm-tab-content">
          <div className="pm-filters">
            <input
              className="pm-input pm-search"
              placeholder="Search by check#, party, bank..."
              value={checkSearch}
              onChange={(e) => { setCheckSearch(e.target.value); setCheckPage(0); }}
            />
            <select className="pm-select" value={checkTypeFilter} onChange={(e) => { setCheckTypeFilter(e.target.value); setCheckPage(0); }}>
              <option value="">All Types</option>
              <option value="ISSUED">Issued</option>
              <option value="RECEIVED">Received</option>
            </select>
            <select className="pm-select" value={checkStatusFilter} onChange={(e) => { setCheckStatusFilter(e.target.value); setCheckPage(0); }}>
              <option value="">All Statuses</option>
              {CHECK_STATUSES.map((s) => <option key={s} value={s}>{formatEnum(s)}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="pm-loading">Loading...</div>
          ) : checkList.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-icon">—</div>
              <div className="pm-empty-title">No Checks</div>
              <div className="pm-empty-sub">Log issued or received checks here.</div>
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>Check #</th>
                    <th>Bank</th>
                    <th>Type</th>
                    <th>Party</th>
                    <th>Amount</th>
                    <th>Issue Date</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {checkList.map((c) => {
                    const due = c.daysUntilDue != null ? daysUntilLabel(c.daysUntilDue) : null;
                    const isActive = !['CLEARED','CANCELLED','STOPPED'].includes(c.status);
                    return (
                      <tr key={c.id} className={isActive && c.daysUntilDue <= 3 ? 'pm-row-warn' : ''}>
                        <td><span className="pm-mono">{c.checkNumber}</span></td>
                        <td>{c.bankName}</td>
                        <td><StatusBadge value={c.checkType} colorMap={CHECK_TYPE_COLORS} /></td>
                        <td>
                          <div className="pm-party-name">{c.partyName}</div>
                          <div className="pm-party-type">{formatEnum(c.partyType)}</div>
                        </td>
                        <td className="pm-amount">{formatCurrency(c.amount, c.currency)}</td>
                        <td>{formatDate(c.issueDate)}</td>
                        <td>
                          <div>{formatDate(c.dueDate)}</div>
                          {due && isActive && (
                            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, fontWeight: 600, background: due.bg, color: due.color }}>
                              {due.label}
                            </span>
                          )}
                        </td>
                        <td><StatusBadge value={c.status} colorMap={CHECK_STATUS_COLORS} /></td>
                        <td>
                          <button className="pm-btn-link" onClick={() => openDetail(c, 'check')}>View</button>
                          <button className="pm-btn-link pm-link-edit" onClick={() => openEditCheck(c)}>Edit</button>
                          {isActive && (
                            <button className="pm-btn-link pm-link-muted" onClick={() => openStatusModal(c, 'check')}>Status</button>
                          )}
                          <button className="pm-btn-link pm-link-danger" onClick={() => openDeleteModal(c, 'check')}>Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {checkTotal > 20 && (
            <div className="pm-pagination">
              <button disabled={checkPage === 0} onClick={() => setCheckPage(p => p - 1)} className="pm-btn pm-btn-sm">Prev</button>
              <span>Page {checkPage + 1} of {Math.ceil(checkTotal / 20)}</span>
              <button disabled={(checkPage + 1) * 20 >= checkTotal} onClick={() => setCheckPage(p => p + 1)} className="pm-btn pm-btn-sm">Next</button>
            </div>
          )}
        </div>
      )}

      {/* ============ PAY ORDERS TAB ============ */}
      {activeTab === 'payorders' && (
        <div className="pm-tab-content">
          <div className="pm-filters">
            <input
              className="pm-input pm-search"
              placeholder="Search by number, beneficiary, bank..."
              value={poSearch}
              onChange={(e) => { setPoSearch(e.target.value); setPoPage(0); }}
            />
            <select className="pm-select" value={poStatusFilter} onChange={(e) => { setPoStatusFilter(e.target.value); setPoPage(0); }}>
              <option value="">All Statuses</option>
              <option value="ISSUED">Issued</option>
              <option value="CLEARED">Cleared</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          {loading ? (
            <div className="pm-loading">Loading...</div>
          ) : payOrderList.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-icon">—</div>
              <div className="pm-empty-title">No Pay Orders</div>
              <div className="pm-empty-sub">Track bank pay orders issued here.</div>
            </div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>PO Number</th>
                    <th>Bank</th>
                    <th>Beneficiary</th>
                    <th>Amount</th>
                    <th>Issue Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payOrderList.map((po) => (
                    <tr key={po.id}>
                      <td><span className="pm-mono">{po.payOrderNumber}</span></td>
                      <td>{po.bankName}</td>
                      <td>
                        <div className="pm-party-name">{po.beneficiaryName}</div>
                        {po.beneficiaryBank && <div className="pm-party-type">{po.beneficiaryBank}</div>}
                      </td>
                      <td className="pm-amount">{formatCurrency(po.amount, po.currency)}</td>
                      <td>{formatDate(po.issueDate)}</td>
                      <td><StatusBadge value={po.status} colorMap={PAY_ORDER_STATUS_COLORS} /></td>
                      <td>
                        <button className="pm-btn-link" onClick={() => openDetail(po, 'payorder')}>View</button>
                        <button className="pm-btn-link pm-link-edit" onClick={() => openEditPayOrder(po)}>Edit</button>
                        {po.status === 'ISSUED' && (
                          <button className="pm-btn-link pm-link-muted" onClick={() => openStatusModal(po, 'payorder')}>Status</button>
                        )}
                        <button className="pm-btn-link pm-link-danger" onClick={() => openDeleteModal(po, 'payorder')}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {poTotal > 20 && (
            <div className="pm-pagination">
              <button disabled={poPage === 0} onClick={() => setPoPage(p => p - 1)} className="pm-btn pm-btn-sm">Prev</button>
              <span>Page {poPage + 1} of {Math.ceil(poTotal / 20)}</span>
              <button disabled={(poPage + 1) * 20 >= poTotal} onClick={() => setPoPage(p => p + 1)} className="pm-btn pm-btn-sm">Next</button>
            </div>
          )}
        </div>
      )}

      {/* ============ CASH TRANSACTION MODAL ============ */}
      {showCashModal && (
        <div className="pm-modal-overlay" onClick={() => { setShowCashModal(false); setEditingItem(null); }}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingItem ? `Edit — ${editingItem.transactionNumber}` : 'New Cash Entry'}</h2>
              <button className="pm-modal-close" onClick={() => { setShowCashModal(false); setEditingItem(null); }}>×</button>
            </div>
            <form onSubmit={handleCreateCash} className="pm-modal-body">
              {formError && <div className="pm-alert pm-alert-error">{formError}</div>}
              <div className="pm-form-grid-2">
                <div className="pm-field">
                  <label>Type *</label>
                  <select className="pm-select pm-full" value={cashForm.type} onChange={(e) => setCashForm({ ...cashForm, type: e.target.value })}>
                    <option value="CASH_IN">Cash In</option>
                    <option value="CASH_OUT">Cash Out</option>
                  </select>
                </div>
                <div className="pm-field">
                  <label>Date *</label>
                  <input type="date" className="pm-input pm-full" value={cashForm.transactionDate}
                    onChange={(e) => setCashForm({ ...cashForm, transactionDate: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Amount *</label>
                  <input type="number" className="pm-input pm-full" placeholder="0.00" min="0.01" step="0.01"
                    value={cashForm.amount} onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Currency</label>
                  <select className="pm-select pm-full" value={cashForm.currency} onChange={(e) => setCashForm({ ...cashForm, currency: e.target.value })}>
                    <option>PKR</option><option>USD</option>
                  </select>
                </div>
                <div className="pm-field">
                  <label>Party Name *</label>
                  <input type="text" className="pm-input pm-full" placeholder="Vendor / customer name"
                    value={cashForm.counterpartyName} onChange={(e) => setCashForm({ ...cashForm, counterpartyName: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Party Type *</label>
                  <select className="pm-select pm-full" value={cashForm.counterpartyType} onChange={(e) => setCashForm({ ...cashForm, counterpartyType: e.target.value })}>
                    {PARTY_TYPES.map((t) => <option key={t} value={t}>{formatEnum(t)}</option>)}
                  </select>
                </div>
                <div className="pm-field pm-span-2">
                  <label>Category *</label>
                  <select className="pm-select pm-full" value={cashForm.category} onChange={(e) => setCashForm({ ...cashForm, category: e.target.value })}>
                    {CASH_CATEGORIES.map((c) => <option key={c} value={c}>{formatEnum(c)}</option>)}
                  </select>
                </div>
                <div className="pm-field">
                  <label>Reference Number</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={cashForm.referenceNumber} onChange={(e) => setCashForm({ ...cashForm, referenceNumber: e.target.value })} />
                </div>
                <div className="pm-field pm-span-2">
                  <label>Description</label>
                  <textarea className="pm-textarea pm-full" rows={2} placeholder="Details about this transaction"
                    value={cashForm.description} onChange={(e) => setCashForm({ ...cashForm, description: e.target.value })} />
                </div>
                <div className="pm-field pm-span-2">
                  <label>Notes</label>
                  <textarea className="pm-textarea pm-full" rows={2} placeholder="Internal notes"
                    value={cashForm.notes} onChange={(e) => setCashForm({ ...cashForm, notes: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Link to GD</label>
                  <select className="pm-select pm-full" value={cashForm.linkedGDId} onChange={(e) => setCashForm({ ...cashForm, linkedGDId: e.target.value })}>
                    <option value="">— None —</option>
                    {gdOptions.map(gd => <option key={gd.id} value={gd.id}>{gd.gdNumber} — {gd.consigneeName} ({formatCurrency(gd.invoiceValue, 'USD')})</option>)}
                  </select>
                </div>
                <div className="pm-field">
                  <label>Link to Invoice</label>
                  <select className="pm-select pm-full" value={cashForm.linkedInvoiceId} onChange={(e) => setCashForm({ ...cashForm, linkedInvoiceId: e.target.value })}>
                    <option value="">— None —</option>
                    {invoiceOptions.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber} — {inv.customerName || 'N/A'} ({formatCurrency(inv.totalAmount, inv.currency)})</option>)}
                  </select>
                </div>
              </div>
              {editingItem?.id && <AttachmentPanel entityType="CASH" entityId={editingItem.id} />}
              <div className="pm-modal-footer">
                <button type="button" className="pm-btn pm-btn-outline" onClick={() => { setShowCashModal(false); setEditingItem(null); }}>Cancel</button>
                <button type="submit" className="pm-btn pm-btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : editingItem ? 'Update Entry' : 'Save Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============ CHECK MODAL ============ */}
      {showCheckModal && (
        <div className="pm-modal-overlay" onClick={() => { setShowCheckModal(false); setEditingItem(null); }}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingItem ? `Edit Check — ${editingItem.checkNumber}` : 'Add Check'}</h2>
              <button className="pm-modal-close" onClick={() => { setShowCheckModal(false); setEditingItem(null); }}>×</button>
            </div>
            <form onSubmit={handleCreateCheck} className="pm-modal-body">
              {formError && <div className="pm-alert pm-alert-error">{formError}</div>}
              <div className="pm-form-grid-2">
                <div className="pm-field">
                  <label>Check Type *</label>
                  <select className="pm-select pm-full" value={checkForm.checkType} onChange={(e) => setCheckForm({ ...checkForm, checkType: e.target.value })}>
                    <option value="ISSUED">Issued (we gave)</option>
                    <option value="RECEIVED">Received (we got)</option>
                  </select>
                </div>
                <div className="pm-field">
                  <label>Check Number *</label>
                  <input type="text" className="pm-input pm-full" placeholder="e.g. 1234567"
                    value={checkForm.checkNumber} onChange={(e) => setCheckForm({ ...checkForm, checkNumber: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Bank Name *</label>
                  <input type="text" className="pm-input pm-full" placeholder="e.g. HBL, UBL, MCB"
                    value={checkForm.bankName} onChange={(e) => setCheckForm({ ...checkForm, bankName: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Account Number</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={checkForm.accountNumber} onChange={(e) => setCheckForm({ ...checkForm, accountNumber: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Amount *</label>
                  <input type="number" className="pm-input pm-full" placeholder="0.00" min="0.01" step="0.01"
                    value={checkForm.amount} onChange={(e) => setCheckForm({ ...checkForm, amount: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Currency</label>
                  <select className="pm-select pm-full" value={checkForm.currency} onChange={(e) => setCheckForm({ ...checkForm, currency: e.target.value })}>
                    <option>PKR</option><option>USD</option>
                  </select>
                </div>
                <div className="pm-field">
                  <label>Party Name *</label>
                  <input type="text" className="pm-input pm-full" placeholder="Payee or payer"
                    value={checkForm.partyName} onChange={(e) => setCheckForm({ ...checkForm, partyName: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Party Type *</label>
                  <select className="pm-select pm-full" value={checkForm.partyType} onChange={(e) => setCheckForm({ ...checkForm, partyType: e.target.value })}>
                    {PARTY_TYPES.map((t) => <option key={t} value={t}>{formatEnum(t)}</option>)}
                  </select>
                </div>
                <div className="pm-field">
                  <label>Issue Date *</label>
                  <input type="date" className="pm-input pm-full" value={checkForm.issueDate}
                    onChange={(e) => setCheckForm({ ...checkForm, issueDate: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Due Date *</label>
                  <input type="date" className="pm-input pm-full" value={checkForm.dueDate}
                    onChange={(e) => setCheckForm({ ...checkForm, dueDate: e.target.value })} required />
                </div>
                <div className="pm-field pm-span-2">
                  <label>Purpose</label>
                  <textarea className="pm-textarea pm-full" rows={2} placeholder="What is this check for?"
                    value={checkForm.purpose} onChange={(e) => setCheckForm({ ...checkForm, purpose: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Reference Number</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={checkForm.referenceNumber} onChange={(e) => setCheckForm({ ...checkForm, referenceNumber: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Notes</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={checkForm.notes} onChange={(e) => setCheckForm({ ...checkForm, notes: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Link to GD</label>
                  <select className="pm-select pm-full" value={checkForm.linkedGDId} onChange={(e) => setCheckForm({ ...checkForm, linkedGDId: e.target.value })}>
                    <option value="">— None —</option>
                    {gdOptions.map(gd => <option key={gd.id} value={gd.id}>{gd.gdNumber} — {gd.consigneeName} ({formatCurrency(gd.invoiceValue, 'USD')})</option>)}
                  </select>
                </div>
                <div className="pm-field">
                  <label>Link to Invoice</label>
                  <select className="pm-select pm-full" value={checkForm.linkedInvoiceId} onChange={(e) => setCheckForm({ ...checkForm, linkedInvoiceId: e.target.value })}>
                    <option value="">— None —</option>
                    {invoiceOptions.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber} — {inv.customerName || 'N/A'} ({formatCurrency(inv.totalAmount, inv.currency)})</option>)}
                  </select>
                </div>
              </div>
              {editingItem?.id && <AttachmentPanel entityType="CHECK" entityId={editingItem.id} />}
              <div className="pm-modal-footer">
                <button type="button" className="pm-btn pm-btn-outline" onClick={() => { setShowCheckModal(false); setEditingItem(null); }}>Cancel</button>
                <button type="submit" className="pm-btn pm-btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : editingItem ? 'Update Check' : 'Save Check'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============ PAY ORDER MODAL ============ */}
      {showPoModal && (
        <div className="pm-modal-overlay" onClick={() => { setShowPoModal(false); setEditingItem(null); }}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>{editingItem ? `Edit Pay Order — ${editingItem.payOrderNumber}` : 'New Pay Order'}</h2>
              <button className="pm-modal-close" onClick={() => { setShowPoModal(false); setEditingItem(null); }}>×</button>
            </div>
            <form onSubmit={handleCreatePayOrder} className="pm-modal-body">
              {formError && <div className="pm-alert pm-alert-error">{formError}</div>}
              <div className="pm-form-grid-2">
                <div className="pm-field">
                  <label>Bank Name *</label>
                  <input type="text" className="pm-input pm-full" placeholder="e.g. HBL, UBL"
                    value={poForm.bankName} onChange={(e) => setPoForm({ ...poForm, bankName: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Issue Date *</label>
                  <input type="date" className="pm-input pm-full" value={poForm.issueDate}
                    onChange={(e) => setPoForm({ ...poForm, issueDate: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Amount *</label>
                  <input type="number" className="pm-input pm-full" placeholder="0.00" min="0.01" step="0.01"
                    value={poForm.amount} onChange={(e) => setPoForm({ ...poForm, amount: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Currency</label>
                  <select className="pm-select pm-full" value={poForm.currency} onChange={(e) => setPoForm({ ...poForm, currency: e.target.value })}>
                    <option>PKR</option><option>USD</option>
                  </select>
                </div>
                <div className="pm-field">
                  <label>Beneficiary Name *</label>
                  <input type="text" className="pm-input pm-full" placeholder="Who receives this?"
                    value={poForm.beneficiaryName} onChange={(e) => setPoForm({ ...poForm, beneficiaryName: e.target.value })} required />
                </div>
                <div className="pm-field">
                  <label>Beneficiary Bank</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={poForm.beneficiaryBank} onChange={(e) => setPoForm({ ...poForm, beneficiaryBank: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Beneficiary Account</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={poForm.beneficiaryAccount} onChange={(e) => setPoForm({ ...poForm, beneficiaryAccount: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Reference Number</label>
                  <input type="text" className="pm-input pm-full" placeholder="Optional"
                    value={poForm.referenceNumber} onChange={(e) => setPoForm({ ...poForm, referenceNumber: e.target.value })} />
                </div>
                <div className="pm-field pm-span-2">
                  <label>Purpose</label>
                  <textarea className="pm-textarea pm-full" rows={2} placeholder="What is this pay order for?"
                    value={poForm.purpose} onChange={(e) => setPoForm({ ...poForm, purpose: e.target.value })} />
                </div>
                <div className="pm-field pm-span-2">
                  <label>Notes</label>
                  <textarea className="pm-textarea pm-full" rows={2} placeholder="Internal notes"
                    value={poForm.notes} onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} />
                </div>
                <div className="pm-field">
                  <label>Link to GD</label>
                  <select className="pm-select pm-full" value={poForm.linkedGDId} onChange={(e) => setPoForm({ ...poForm, linkedGDId: e.target.value })}>
                    <option value="">— None —</option>
                    {gdOptions.map(gd => <option key={gd.id} value={gd.id}>{gd.gdNumber} — {gd.consigneeName} ({formatCurrency(gd.invoiceValue, 'USD')})</option>)}
                  </select>
                </div>
                <div className="pm-field">
                  <label>Link to Invoice</label>
                  <select className="pm-select pm-full" value={poForm.linkedInvoiceId} onChange={(e) => setPoForm({ ...poForm, linkedInvoiceId: e.target.value })}>
                    <option value="">— None —</option>
                    {invoiceOptions.map(inv => <option key={inv.id} value={inv.id}>{inv.invoiceNumber} — {inv.customerName || 'N/A'} ({formatCurrency(inv.totalAmount, inv.currency)})</option>)}
                  </select>
                </div>
              </div>
              {editingItem?.id && <AttachmentPanel entityType="PAY_ORDER" entityId={editingItem.id} />}
              <div className="pm-modal-footer">
                <button type="button" className="pm-btn pm-btn-outline" onClick={() => { setShowPoModal(false); setEditingItem(null); }}>Cancel</button>
                <button type="submit" className="pm-btn pm-btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : editingItem ? 'Update Pay Order' : 'Save Pay Order'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============ STATUS UPDATE MODAL ============ */}
      {showStatusModal && statusItem && (
        <div className="pm-modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="pm-modal pm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>Update Status</h2>
              <button className="pm-modal-close" onClick={() => setShowStatusModal(false)}>×</button>
            </div>
            <div className="pm-modal-body">
              <p className="pm-status-item-info">
                {statusItemType === 'check'
                  ? `Check ${statusItem.checkNumber} — ${statusItem.partyName}`
                  : `Pay Order ${statusItem.payOrderNumber} — ${statusItem.beneficiaryName}`}
              </p>
              <div className="pm-field">
                <label>New Status</label>
                <select className="pm-select pm-full" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  {statusItemType === 'check'
                    ? CHECK_STATUSES.map((s) => <option key={s} value={s}>{formatEnum(s)}</option>)
                    : ['ISSUED','CLEARED','CANCELLED'].map((s) => <option key={s} value={s}>{formatEnum(s)}</option>)
                  }
                </select>
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn pm-btn-outline" onClick={() => setShowStatusModal(false)}>Cancel</button>
              <button className="pm-btn pm-btn-primary" onClick={handleUpdateStatus} disabled={statusLoading}>
                {statusLoading ? 'Saving...' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ DETAIL MODAL ============ */}
      {/* ============ DELETE CONFIRM MODAL ============ */}
      {showDeleteModal && deleteItem && (
        <div className="pm-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="pm-modal pm-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>Confirm Delete</h2>
              <button className="pm-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="pm-modal-body">
              <p style={{ margin: 0, fontSize: 14, color: 'var(--color-neutral-700)', lineHeight: 1.6 }}>
                Are you sure you want to delete{' '}
                <strong>
                  {deleteItemType === 'cash' && deleteItem.transactionNumber}
                  {deleteItemType === 'check' && `Check ${deleteItem.checkNumber}`}
                  {deleteItemType === 'payorder' && deleteItem.payOrderNumber}
                </strong>
                ? This action <strong>cannot be undone</strong>.
              </p>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn pm-btn-outline" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="pm-btn pm-btn-danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailModal && detailItem && (
        <div className="pm-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h2>
                {detailType === 'cash' && `Cash Transaction — ${detailItem.transactionNumber}`}
                {detailType === 'check' && `Check — ${detailItem.checkNumber}`}
                {detailType === 'payorder' && `Pay Order — ${detailItem.payOrderNumber}`}
              </h2>
              <button className="pm-modal-close" onClick={() => setShowDetailModal(false)}>×</button>
            </div>
            <div className="pm-modal-body">
              <div className="pm-detail-grid">
                {detailType === 'cash' && <>
                  <DetailRow label="Transaction #" value={detailItem.transactionNumber} mono />
                  <DetailRow label="Type" value={<StatusBadge value={detailItem.type} colorMap={CASH_TYPE_COLORS} />} />
                  <DetailRow label="Date" value={formatDate(detailItem.transactionDate)} />
                  <DetailRow label="Amount" value={formatCurrency(detailItem.amount, detailItem.currency)} />
                  <DetailRow label="Party" value={`${detailItem.counterpartyName} (${formatEnum(detailItem.counterpartyType)})`} />
                  <DetailRow label="Category" value={formatEnum(detailItem.category)} />
                  <DetailRow label="Description" value={detailItem.description} />
                  <DetailRow label="Reference" value={detailItem.referenceNumber} />
                  <DetailRow label="Notes" value={detailItem.notes} />
                  <DetailRow label="Payment Mode" value={formatEnum(detailItem.paymentMode) || '—'} />
                  <DetailRow label="Linked GD" value={detailItem.linkedGDNumber || '—'} />
                  <DetailRow label="Linked Invoice" value={detailItem.linkedInvoiceNumber ? `#${detailItem.linkedInvoiceNumber}` : '—'} />
                  <DetailRow label="Created By" value={detailItem.createdByName} />
                  <DetailRow label="Created At" value={detailItem.createdAt ? new Date(detailItem.createdAt).toLocaleString() : '—'} />
                  {detailItem.paymentDetails && Object.keys(detailItem.paymentDetails).length > 0 && (
                    <div style={{ gridColumn: '1 / -1', marginTop: 8, background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-neutral-700)', marginBottom: 8 }}>Bank / Mode Details</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
                        {Object.entries(detailItem.paymentDetails).map(([k, v]) => (
                          <div key={k}>
                            <span style={{ color: 'var(--color-text-tertiary)' }}>{k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}: </span>
                            <span style={{ color: 'var(--color-neutral-700)', fontWeight: 500 }}>{String(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>}
                {detailType === 'check' && <>
                  <DetailRow label="Check #" value={detailItem.checkNumber} mono />
                  <DetailRow label="Type" value={<StatusBadge value={detailItem.checkType} colorMap={CHECK_TYPE_COLORS} />} />
                  <DetailRow label="Status" value={<StatusBadge value={detailItem.status} colorMap={CHECK_STATUS_COLORS} />} />
                  <DetailRow label="Bank" value={`${detailItem.bankName}${detailItem.accountNumber ? ' · ' + detailItem.accountNumber : ''}`} />
                  <DetailRow label="Amount" value={formatCurrency(detailItem.amount, detailItem.currency)} />
                  <DetailRow label="Party" value={`${detailItem.partyName} (${formatEnum(detailItem.partyType)})`} />
                  <DetailRow label="Issue Date" value={formatDate(detailItem.issueDate)} />
                  <DetailRow label="Due Date" value={formatDate(detailItem.dueDate)} />
                  <DetailRow label="Cleared On" value={formatDate(detailItem.clearanceDate)} />
                  <DetailRow label="Purpose" value={detailItem.purpose} />
                  <DetailRow label="Reference" value={detailItem.referenceNumber} />
                  <DetailRow label="Notes" value={detailItem.notes} />
                  <DetailRow label="Linked GD" value={detailItem.linkedGDNumber || '—'} />
                  <DetailRow label="Linked Invoice" value={detailItem.linkedInvoiceNumber ? `#${detailItem.linkedInvoiceNumber}` : '—'} />
                  <DetailRow label="Created By" value={detailItem.createdByName} />
                </>}
                {detailType === 'payorder' && <>
                  <DetailRow label="PO Number" value={detailItem.payOrderNumber} mono />
                  <DetailRow label="Status" value={<StatusBadge value={detailItem.status} colorMap={PAY_ORDER_STATUS_COLORS} />} />
                  <DetailRow label="Bank" value={detailItem.bankName} />
                  <DetailRow label="Amount" value={formatCurrency(detailItem.amount, detailItem.currency)} />
                  <DetailRow label="Beneficiary" value={detailItem.beneficiaryName} />
                  <DetailRow label="Beneficiary Bank" value={detailItem.beneficiaryBank} />
                  <DetailRow label="Beneficiary Account" value={detailItem.beneficiaryAccount} />
                  <DetailRow label="Issue Date" value={formatDate(detailItem.issueDate)} />
                  <DetailRow label="Purpose" value={detailItem.purpose} />
                  <DetailRow label="Reference" value={detailItem.referenceNumber} />
                  <DetailRow label="Notes" value={detailItem.notes} />
                  <DetailRow label="Linked GD" value={detailItem.linkedGDNumber || '—'} />
                  <DetailRow label="Linked Invoice" value={detailItem.linkedInvoiceNumber ? `#${detailItem.linkedInvoiceNumber}` : '—'} />
                  <DetailRow label="Created By" value={detailItem.createdByName} />
                </>}
              </div>
            </div>
            <div className="pm-modal-footer">
              <button className="pm-btn pm-btn-outline" onClick={() => setShowDetailModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="pm-detail-row">
      <span className="pm-detail-label">{label}</span>
      <span className={`pm-detail-value ${mono ? 'pm-mono' : ''}`}>
        {value || '—'}
      </span>
    </div>
  );
}
